import React, { useEffect, useMemo, useState } from "react";
import { NavLink, Link } from "react-router-dom";
import Logo from "../assets/zeamays.png";
import { useAuth } from "../context/AuthContext";
import { useCart } from "../context/cartcontext";
import { isAdminUser } from "../services/admin";

const Navbar = () => {
  const { user, loading, signingOut, signOut } = useAuth();
  const { cartCount } = useCart();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [avatarFailed, setAvatarFailed] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  const avatarUrl = user?.user_metadata?.avatar_url;
  const userLabel = useMemo(() => {
    return (
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.email ||
      "Profile"
    );
  }, [user]);

  useEffect(() => {
    setAvatarFailed(false);
  }, [avatarUrl]);

  useEffect(() => {
    let cancelled = false;

    const verifyAdmin = async () => {
      if (!user) {
        if (!cancelled) {
          setIsAdmin(false);
        }
        return;
      }

      try {
        const adminAccess = await isAdminUser(user);
        if (!cancelled) {
          setIsAdmin(adminAccess);
        }
      } catch (error) {
        if (!cancelled) {
          setIsAdmin(false);
        }
      }
    };

    verifyAdmin();

    return () => {
      cancelled = true;
    };
  }, [user]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen((current) => !current);
  };

  const toggleProfileMenu = () => {
    setIsProfileMenuOpen((current) => !current);
  };

  const closeMenus = () => {
    setIsMobileMenuOpen(false);
    setIsProfileMenuOpen(false);
  };

  const renderAvatar = () => {
    if (avatarUrl && !avatarFailed) {
      return (
        <img
          src={avatarUrl}
          alt={userLabel}
          className="h-10 w-10 rounded-full object-cover"
          width="40"
          height="40"
          loading="lazy"
          decoding="async"
          onError={() => setAvatarFailed(true)}
        />
      );
    }

    return (
      <div className="flex h-10 w-10 items-center justify-center rounded-full bg-red-700 text-sm font-bold text-white">
        {userLabel.charAt(0).toUpperCase()}
      </div>
    );
  };

  return (
    <nav className="fixed left-0 top-0 z-50 w-full border-b border-white/60 bg-white/85 backdrop-blur-xl shadow-[0_10px_30px_rgba(15,23,42,0.08)]">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="flex h-16 items-center justify-between">
          <div className="flex flex-shrink-0 items-center gap-3">
            <img
              src={Logo}
              alt="Zea Mays Logo"
              className="h-10 w-10 rounded-full object-contain shadow-sm ring-1 ring-lime-100"
              width="40"
              height="40"
              loading="eager"
              decoding="sync"
            />
            <div className="hidden sm:block">
              <p className="text-[11px] font-semibold uppercase tracking-[0.28em] text-red-700">
                Zea Mays Foods
              </p>
              <p className="text-xs text-slate-500">Nigerian food products. Natural. Local. Delicious.</p>
            </div>
          </div>

          <div className="hidden items-center space-x-1 rounded-full border border-lime-100 bg-white/90 px-2 py-1 shadow-sm sm:flex">
            <NavLink to="/" className={({ isActive }) => navLinkClass(isActive)}>
              Home
            </NavLink>
            <NavLink to="/about" className={({ isActive }) => navLinkClass(isActive)}>
              About
            </NavLink>
            <NavLink to="/products" className={({ isActive }) => navLinkClass(isActive)}>
              Products
            </NavLink>
            <NavLink to="/blog" className={({ isActive }) => navLinkClass(isActive)}>
              Blog
            </NavLink>
            <NavLink to="/contact" className={({ isActive }) => navLinkClass(isActive)}>
              Contact
            </NavLink>
            <NavLink to="/reviews" className={({ isActive }) => navLinkClass(isActive)}>
              Reviews
            </NavLink>
            <Link to="/cart" className="relative rounded-full px-3 py-2 text-sm font-semibold text-red-700 transition hover:bg-lime-50 hover:text-red-800">
              Cart
              {cartCount > 0 ? (
                <span className="absolute -right-4 -top-2 inline-flex min-h-5 min-w-5 items-center justify-center rounded-full bg-red-700 px-1 text-xs font-semibold text-white">
                  {cartCount}
                </span>
              ) : null}
            </Link>
            {isAdmin ? (
              <NavLink
                to="/admin"
                className="rounded-full bg-slate-950 px-4 py-2 text-sm font-semibold text-white transition hover:bg-red-700"
              >
                Admin Dashboard
              </NavLink>
            ) : null}

            {loading ? (
              <div className="ml-4 h-10 w-10 rounded-full bg-white/70" />
            ) : user ? (
              <div className="relative ml-4">
                <button
                  onClick={toggleProfileMenu}
                  className="flex items-center gap-3 rounded-full border border-red-200 bg-white/80 px-2 py-1 transition hover:border-green-300"
                >
                  {renderAvatar()}
                </button>

                {isProfileMenuOpen ? (
                  <div className="absolute right-0 mt-3 w-48 rounded-2xl bg-white p-2 shadow-xl">
                    <Link
                      to="/profile"
                      onClick={closeMenus}
                      className="block rounded-xl px-4 py-3 text-sm font-medium text-red-700 transition hover:bg-green-50"
                    >
                      Profile
                    </Link>
                    <Link
                      to="/order"
                      onClick={closeMenus}
                      className="block rounded-xl px-4 py-3 text-sm font-medium text-red-700 transition hover:bg-green-50"
                    >
                      Orders
                    </Link>
                    <button
                      onClick={async () => {
                        closeMenus();
                        await signOut();
                      }}
                      disabled={signingOut}
                      className="block w-full rounded-xl px-4 py-3 text-left text-sm font-medium text-red-700 transition hover:bg-green-50"
                    >
                      {signingOut ? "Signing out..." : "Logout"}
                    </button>
                  </div>
                ) : null}
              </div>
            ) : (
              <NavLink
                to="/login"
                className="ml-2 rounded-full bg-red-700 px-4 py-2 text-sm font-semibold text-white transition duration-300 hover:bg-red-800"
              >
                Sign In
              </NavLink>
            )}
          </div>

          <div className="sm:hidden">
            <button
              onClick={toggleMobileMenu}
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-lime-200 bg-white text-slate-700 shadow-sm focus:outline-none"
              aria-label="Toggle navigation"
            >
              <svg className="h-8 w-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {isMobileMenuOpen && (
        <div className="border-t border-lime-100 bg-white/95 px-4 py-3 shadow-lg backdrop-blur sm:hidden">
          <div className="space-y-2 rounded-2xl border border-lime-100 bg-slate-50 p-3">
          <NavLink to="/" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            Home
          </NavLink>
          <NavLink to="/about" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            About
          </NavLink>
          <NavLink to="/products" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            Products
          </NavLink>
          <NavLink to="/blog" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            Blog
          </NavLink>
          <NavLink to="/contact" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            Contact
          </NavLink>
          <NavLink to="/reviews" className={({ isActive }) => mobileLinkClass(isActive)} onClick={toggleMobileMenu}>
            Reviews
          </NavLink>
          <Link to="/cart" className="block rounded-xl px-3 py-2 font-medium text-red-700 hover:bg-white" onClick={toggleMobileMenu}>
            Cart {cartCount > 0 ? `(${cartCount})` : ""}
          </Link>
          {isAdmin ? (
            <Link
              to="/admin"
              className="block rounded-xl bg-slate-950 px-3 py-2 text-center font-semibold text-white hover:bg-red-700"
              onClick={toggleMobileMenu}
            >
              Admin Dashboard
            </Link>
          ) : null}

          {loading ? null : user ? (
            <>
              <Link
                to="/profile"
                className="flex items-center gap-3 rounded-xl px-3 py-2 text-red-700 hover:bg-white"
                onClick={toggleMobileMenu}
              >
                {renderAvatar()}
                <span className="font-medium">Profile</span>
              </Link>
              <Link
                to="/order"
                className="block rounded-xl px-3 py-2 font-medium text-red-700 hover:bg-white"
                onClick={toggleMobileMenu}
              >
                Orders
              </Link>
              <button
                onClick={async () => {
                  toggleMobileMenu();
                  await signOut();
                }}
                disabled={signingOut}
                className="block w-full rounded-xl bg-red-700 py-2 text-center font-medium text-white hover:bg-red-800"
              >
                {signingOut ? "Signing out..." : "Logout"}
              </button>
            </>
          ) : (
            <NavLink
              to="/login"
              className="block w-full rounded-xl bg-red-700 py-2 text-center font-medium text-white hover:bg-red-800"
              onClick={toggleMobileMenu}
            >
              Sign In
            </NavLink>
          )}
          </div>
        </div>
      )}
    </nav>
  );
};

const navLinkClass = (isActive) =>
  [
    "rounded-full px-4 py-2 text-sm font-semibold transition",
    isActive
      ? "bg-red-700 text-white shadow-sm"
      : "text-slate-700 hover:bg-lime-50 hover:text-red-700",
  ].join(" ");

const mobileLinkClass = (isActive) =>
  [
    "block rounded-xl px-3 py-2 font-medium transition",
    isActive ? "bg-white text-red-700 shadow-sm" : "text-red-700 hover:bg-white",
  ].join(" ");

export default Navbar;
